import type { SharedInventoryFilter } from '@jazel-auto/ise-contracts'

export type ConditionValue = 'new' | 'used' | 'cpo'

export interface GroupSiteFilter {
  condition?: ConditionValue[]
  make?: string
  model?: string
  trim?: string
  yearMin?: number
  yearMax?: number
  bodyType?: string
  drivetrain?: string
  fuelType?: string
  exteriorColor?: string
  interiorColor?: string
  priceMin?: number
  priceMax?: number
  mileageMin?: number
  mileageMax?: number
  keyword?: string
}

export interface GroupSiteSourceEntry {
  sourceJuid: string
  sourceFeed?: string
  useSourceLocation?: boolean
  filter: GroupSiteFilter
}

export type PreProcessorMode = 'groupSite' | 'sharedInventory' | 'none'

export interface GroupSiteConfigResponse {
  mode: PreProcessorMode
  sources: GroupSiteSourceEntry[]
  pattern?: string
  feedSource?: string
  filters?: SharedInventoryFilter[]
  source: 'draft' | 'published' | 'none'
  savedAt?: string
  savedBy?: string
}

export interface AccountFeedWithPattern {
  id: string
  friendlyName: string
  pattern: string
}

export async function fetchGroupSiteConfig(
  endpoint: string,
  jazelId: string,
  opts?: { includeDraft?: boolean; includeFeeds?: boolean; token?: string },
): Promise<{ config: GroupSiteConfigResponse; accountFeeds?: AccountFeedWithPattern[]; accountFeedsError?: string }> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/group-site-config/${encodeURIComponent(jazelId)}`)
  if (opts?.includeDraft) url.searchParams.set('includeDraft', 'true')
  if (opts?.includeFeeds) url.searchParams.set('includeFeeds', 'true')

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), { headers })
  if (!resp.ok) throw new Error(`Failed to load group site config (${resp.status})`)
  const json = (await resp.json().catch(() => ({}))) as Record<string, unknown>
  const data = (json?.data || {}) as GroupSiteConfigResponse
  if (!data.mode) {
    data.mode = Array.isArray(data.sources) && data.sources.length > 0 ? 'groupSite' : 'none'
  }

  const rawFeeds = json?.accountFeeds
  const accountFeeds: AccountFeedWithPattern[] | undefined = Array.isArray(rawFeeds)
    ? rawFeeds
        .filter((f: unknown) => f && typeof f === 'object')
        .map((f: any) => ({
          id: String(f.id || ''),
          friendlyName: String(f.friendlyName || f.id || ''),
          pattern: String(f.pattern || ''),
        }))
        .filter((f) => f.id.length > 0)
    : undefined

  const accountFeedsError = typeof json?.accountFeedsError === 'string' ? json.accountFeedsError : undefined

  return { config: data, accountFeeds, accountFeedsError }
}

export async function fetchGroupSiteConfigDraftSummary(
  endpoint: string,
  jazelId: string,
  opts?: { token?: string },
): Promise<{ hasDraft: boolean; savedAt?: string; savedBy?: string }> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/group-site-config/${encodeURIComponent(jazelId)}`)
  url.searchParams.set('action', 'draftSummary')

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), { headers })
  if (!resp.ok) throw new Error(`Failed to load group site config draft summary (${resp.status})`)
  const json = (await resp.json().catch(() => ({}))) as Record<string, unknown>
  return (json?.data || {}) as { hasDraft: boolean; savedAt?: string; savedBy?: string }
}

export async function saveGroupSiteConfigDraft(
  endpoint: string,
  jazelId: string,
  sources: GroupSiteSourceEntry[],
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/group-site-config/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ mode: 'groupSite', sources }),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save group site config draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function saveSharedInventoryConfigDraft(
  endpoint: string,
  jazelId: string,
  config: { pattern: string; feedSource?: string; filters: SharedInventoryFilter[] },
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/group-site-config/${encodeURIComponent(jazelId)}`)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const body: Record<string, unknown> = {
    mode: 'sharedInventory',
    pattern: config.pattern,
    filters: config.filters,
  }
  if (config.feedSource) body.feedSource = config.feedSource

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to save shared inventory config draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export async function deleteGroupSiteConfigDraft(
  endpoint: string,
  jazelId: string,
  opts?: { token?: string },
): Promise<void> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const accountId = String(jazelId || '').trim()
  const token = String(opts?.token || '').trim()

  if (!base) throw new Error('Missing ISE backend URL')
  if (!accountId) throw new Error('Missing jazelId')
  if (!token) throw new Error('Missing session token')

  const url = `${base}/group-site-config/${encodeURIComponent(accountId)}`
  const resp = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Failed to revert pre-processor config draft (${resp.status}): ${text || 'unknown error'}`)
  }
}

export interface DealerFeedInfo {
  id: string
  friendlyName: string
  type?: string
}

export async function fetchDealerFeeds(
  endpoint: string,
  jazelId: string,
  opts?: { token?: string },
): Promise<DealerFeedInfo[]> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = `${base}/dealer-feeds/${encodeURIComponent(jazelId)}`

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url, { headers })
  if (!resp.ok) return []
  const json = (await resp.json().catch(() => ({}))) as Record<string, unknown>
  const feeds = json?.feeds
  if (Array.isArray(feeds)) {
    return feeds
      .filter((f: unknown) => f && typeof f === 'object')
      .filter((f: any) => f.type !== 'enhancement')
      .map((f: any) => ({
        id: String(f.id || ''),
        friendlyName: String(f.friendlyName || f.id || ''),
        type: typeof f.type === 'string' ? f.type : undefined,
      }))
      .filter((f) => f.id.length > 0)
  }
  return []
}
